import { useEffect, useState, useCallback, useRef } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ChatCircleText, PaperPlaneTilt, Trash, At, Check, ArrowCounterClockwise } from "@phosphor-icons/react";

const fmtWhen = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
};
const initials = (name = "") =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]?.toUpperCase()).join("") || "?";

const esc = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
function renderBody(body, names) {
  if (!names.length) return body;
  const re = new RegExp("(" + names.map((n) => "@" + esc(n)).join("|") + ")", "g");
  return body.split(re).map((part, i) =>
    part.startsWith("@") && names.includes(part.slice(1))
      ? <span key={i} className="text-orangeink font-semibold">{part}</span>
      : <span key={i}>{part}</span>);
}

export default function ReviewNotes({ entityType, entityId, onChange }) {
  const { user } = useAuth();
  const [notes, setNotes] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [body, setBody] = useState("");
  const [mentions, setMentions] = useState([]);
  const [showMention, setShowMention] = useState(false);
  const [saving, setSaving] = useState(false);
  const mentionRef = useRef(null);

  const load = useCallback(async () => {
    if (!entityId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/comments?entity_type=${entityType}&entity_id=${entityId}`);
      setNotes(data.comments || []);
    } catch { setNotes([]); } finally { setLoading(false); }
  }, [entityType, entityId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/team-members").then(({ data }) => setMembers(data.members || [])).catch(() => {}); }, []);
  useEffect(() => {
    const onDoc = (e) => { if (mentionRef.current && !mentionRef.current.contains(e.target)) setShowMention(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const memberNames = members.map((m) => m.name);

  const addMention = (m) => {
    setBody((b) => `${b}${b && !b.endsWith(" ") ? " " : ""}@${m.name} `);
    setMentions((arr) => (arr.includes(m.email) ? arr : [...arr, m.email]));
    setShowMention(false);
  };

  const post = async () => {
    if (!body.trim()) return;
    setSaving(true);
    try {
      const used = mentions.filter((email) => {
        const m = members.find((x) => x.email === email);
        return m && body.includes(`@${m.name}`);
      });
      await api.post("/comments", { entity_type: entityType, entity_id: entityId, body: body.trim(), mentions: used });
      setBody(""); setMentions([]);
      await load(); onChange?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not add note");
    } finally { setSaving(false); }
  };

  const del = async (id) => {
    try { await api.delete(`/comments/${id}`); await load(); onChange?.(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not delete note"); }
  };

  const toggleResolve = async (n) => {
    try {
      await api.post(`/comments/${n.id}/${n.resolved ? "reopen" : "resolve"}`);
      await load(); onChange?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not update note"); }
  };

  const canDelete = (n) => n.author_email === user?.email || user?.role === "admin";

  return (
    <div data-testid="review-notes">
      <div className="flex items-center gap-2 overline text-navy/65 mb-3">
        <ChatCircleText size={15} /> Review notes {notes.length > 0 && `· ${notes.length}`}
      </div>

      {loading ? (
        <div className="text-xs font-mono text-navy/35 tracking-widest py-2">LOADING…</div>
      ) : notes.length === 0 ? (
        <p className="text-xs text-navy/65 mb-3" data-testid="notes-empty">No notes yet. Leave a query or sign-off note for the team.</p>
      ) : (
        <div className="space-y-2.5 mb-3">
          {notes.map((n) => (
            <div key={n.id} data-testid={`note-${n.id}`} className="flex gap-2.5 group">
              <div className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-[0.62rem] font-bold ${n.resolved ? "bg-positive/15 text-positive" : "bg-orange/15 text-orangeink"}`}>
                {initials(n.author_name)}
              </div>
              <div className={`flex-1 min-w-0 border rounded-lg px-3 py-2 ${n.resolved ? "bg-positive/[0.04] border-positive/20" : "bg-navy/[0.03] border-slate-200"}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-semibold text-navy truncate flex items-center gap-1.5">
                    {n.author_name}
                    {n.resolved && <span className="text-[0.6rem] font-semibold uppercase tracking-wider text-positive bg-positive/10 px-1.5 py-0.5 rounded">Resolved</span>}
                  </span>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-[0.65rem] text-navy/65 font-mono">{fmtWhen(n.created_at)}</span>
                    <button onClick={() => toggleResolve(n)} data-testid={`note-resolve-${n.id}`}
                      title={n.resolved ? "Re-open" : "Mark resolved"}
                      className="text-navy/25 hover:text-positive opacity-0 group-hover:opacity-100 transition-opacity">
                      {n.resolved ? <ArrowCounterClockwise size={13} /> : <Check size={13} />}
                    </button>
                    {canDelete(n) && (
                      <button onClick={() => del(n.id)} data-testid={`note-delete-${n.id}`}
                        className="text-navy/25 hover:text-negative opacity-0 group-hover:opacity-100 transition-opacity">
                        <Trash size={13} />
                      </button>
                    )}
                  </div>
                </div>
                <div className="text-sm text-navy/80 mt-0.5 whitespace-pre-wrap break-words">{renderBody(n.body, memberNames)}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-end gap-2">
        <div className="flex-1 relative" ref={mentionRef}>
          <textarea value={body} onChange={(e) => setBody(e.target.value)} data-testid="note-input"
            onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) post(); }}
            rows={2} placeholder="Add a note… (⌘/Ctrl + Enter to send)"
            className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 pr-9 text-sm focus:border-orange focus:ring-2 focus:ring-orange/25 outline-none resize-none" />
          <button type="button" onClick={() => setShowMention((s) => !s)} data-testid="note-mention-btn"
            title="Mention a team member"
            className="absolute right-2 top-2 text-navy/35 hover:text-orangeink transition-colors"><At size={16} /></button>
          {showMention && (
            <div className="absolute right-0 bottom-full mb-1 w-56 max-h-56 overflow-y-auto bg-white border border-slate-200 rounded-lg shadow-xl z-30" data-testid="mention-menu">
              {members.length === 0 && <div className="px-3 py-2 text-xs text-navy/65">No team members</div>}
              {members.map((m) => (
                <button key={m.email} onClick={() => addMention(m)} data-testid={`mention-${m.email}`}
                  className="w-full text-left px-3 py-2 text-sm text-navy hover:bg-orange/10 transition-colors">
                  <span className="font-medium">{m.name}</span>
                  <span className="block text-[0.65rem] text-navy/65 truncate">{m.email}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <button onClick={post} disabled={saving || !body.trim()} data-testid="note-submit"
          className="shrink-0 flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium rounded-lg bg-orange text-white hover:bg-navy transition-colors disabled:opacity-50">
          <PaperPlaneTilt size={15} weight="fill" /> {saving ? "…" : "Post"}
        </button>
      </div>
    </div>
  );
}
